import { inject, injectable } from 'inversify';
import { walletBackendUrl } from 'src/config';
import JwtService from 'src/features/auth/data/service/jwtService';
import HttpClient from 'src/features/http';
import {
  GetWalletParams,
  GetWalletTransactionsParams,
  GetAvailableCreditsParams,
  GetAvailableSpentTransactionsParams,
  WalletServiceInterface,
  WalletTransactionsResponse,
  AvailableCreditsResponse,
  GetWalletConfigBySlugParams,
  GlobalConfigResponse,
} from 'src/features/pariWallet/data/service/WalletService.types';
import { PariWallet } from 'src/types/common';

@injectable()
class WalletService implements WalletServiceInterface {
  @inject(HttpClient) private readonly client!: HttpClient;
  @inject(JwtService) private readonly jwtService!: JwtService;

  private getOrGenerateJwt(userUuid: string) {
    return this.jwtService.getOrCreateToken(
      { uuid: userUuid },
      'walletReadToken'
    );
  }

  public async getWallet({ userUuid }: GetWalletParams) {
    const token = await this.getOrGenerateJwt(userUuid);

    return this.client.getClient().get<PariWallet>(`/wallets/${userUuid}/`, {
      baseURL: walletBackendUrl,
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  public async getWalletTransactions({
    userUuid,
    ordering,
    filters,
    limit,
    walletUuid,
    offset,
  }: GetWalletTransactionsParams) {
    const token = await this.getOrGenerateJwt(userUuid);
    const params: Record<string, string | string[] | number> = {
      wallet__uuid: walletUuid,
    };

    if (ordering) params.ordering = ordering;

    if (filters) {
      const { types, startDate, endDate } = filters;

      if (types && types.length > 0) params.type__slug_name = types.join(',');
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
    }

    if (limit) params.limit = limit;
    if (offset) params.offset = offset;

    return this.client
      .getClient()
      .get<WalletTransactionsResponse>(`/transactions/`, {
        baseURL: walletBackendUrl,
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
  }

  public async getAvailableCredits({
    userUuid,
    totalOrder,
  }: GetAvailableCreditsParams) {
    const token = await this.getOrGenerateJwt(userUuid);

    return this.client
      .getClient()
      .get<AvailableCreditsResponse>(
        `/credits/available/${userUuid}?total_order=${totalOrder}`,
        {
          baseURL: walletBackendUrl,
          headers: { Authorization: `Bearer ${token}` },
        }
      );
  }

  public async getAvailableTransactions({
    userUuid,
    ordering,
    filters,
    walletUuid,
    limit,
    offset,
  }: GetAvailableSpentTransactionsParams) {
    const token = await this.getOrGenerateJwt(userUuid);
    const params: Record<string, string | string[] | number> = {
      wallet__uuid: walletUuid,
    };

    if (ordering) params.ordering = ordering;

    if (filters) {
      const { types, startDate, endDate } = filters;

      if (types && types.length > 0) params.type__slug_name = types.join(',');
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
    }

    if (limit) params.limit = limit;
    if (offset) params.offset = offset;

    return this.client
      .getClient()
      .get<WalletTransactionsResponse>(`/transactions/available/`, {
        baseURL: walletBackendUrl,
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
  }

  public async getSpentTransactions({
    userUuid,
    ordering,
    filters,
    walletUuid,
    limit,
    offset,
  }: GetAvailableSpentTransactionsParams) {
    const token = await this.getOrGenerateJwt(userUuid);
    const params: Record<string, string | string[] | number> = {
      wallet__uuid: walletUuid,
    };

    if (ordering) params.ordering = ordering;

    if (filters) {
      const { types, startDate, endDate } = filters;

      if (types && types.length > 0) params.type__slug_name = types.join(',');
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
    }

    if (limit) params.limit = limit;
    if (offset) params.offset = offset;

    return this.client
      .getClient()
      .get<WalletTransactionsResponse>(`/transactions/spent/`, {
        baseURL: walletBackendUrl,
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
  }

  public async getWalletConfigBySlug<T>({
    userUuid,
    slug,
  }: GetWalletConfigBySlugParams) {
    const token = await this.getOrGenerateJwt(userUuid);

    return this.client
      .getClient()
      .get<GlobalConfigResponse<T>>(`/globalconfig/${slug}/`, {
        baseURL: walletBackendUrl,
        headers: { Authorization: `Bearer ${token}` },
      });
  }
}

export default WalletService;
